import BaseBidder from "../bidders/BaseBidder"
import { AdUnit } from "../types/prebid"
import { BidderKey, PublisherConfig, SkipKey, SkipOption, SlotConfig, SlotEventParams, SlotKey, SlotKeyItterable } from "../types/publisher"
import { deviceType } from "./enums"
import { isHome, mergeDeep, parseSizes, slotKeyToSlotId, getDeviceType } from "./utilities"

const configBlueprint: PublisherConfig = {
	enabled: true,
	loadLegacyScriptWhenDisabled: true,

	debug: false,
	skipFirstInteraction: false,
	skipConsent: false,
	customConsent: false,

	slots: {},
	adUnits: [],

	get isHome() {
		return isHome(this.config.homepages ?? [])
	},

	config: {
		yieldbird: {
			uuid: ""
		},
		
		seller: {
			asi: "adalliance.nl",
			sid: ""
		},

		cxense: {
			enabled: false,
			siteId: ""
		},

		magnite: {
			enabled: false,
			accountId: "",
			endpoint: "",
		},

		gdprSettings: {
			label: 'Privacy instellingen aanpassen',
			inject: {
				target: 'body',
				position: 'beforeend'
			},
		},

		breakpointDesktop: 990,
		breakpointTablet: 768,

		homepages: [
			"/"
		],

		slotPath: "",

		backgroundColor: '#eee',
		customCss: '',
		
		useCustomOutstreamRenderer: true,
	},

	onBeforeStart: () => {},
}

const slot_blueprint = {
	id: null,
	path: null,
	collapseEmptyDivs: true,
	sizes: [],
	takeover: false,
	native: false,
	inject: {
		target: null,
		position: 'afterend'
	},
	type: 'inview',
	margin: false,
	useIdealMargin: false,
	getDefaultCss: ():string => { return '' },
	onSlotInjected: (slot:Slot, { css }:SlotEventParams):void => {css(slot.getDefaultCss())},
	onSlotRendered: (slot:Slot):void => { slot._el.style.display = ''; },
	refresh: false,
	retry: false,
	skip: false
}

const slot_defaults = {
	top: mergeDeep(slot_blueprint, {
		getDefaultCss() { return `
			#${this.id} {
				min-height: 280px;
			}
		`;},
	}),

	mid: mergeDeep(slot_blueprint, {
		getDefaultCss() { return `
			#${this.id} {
				min-height: 400px;
			}
			@media (max-width: 640px) {
				#${this.id} {
					min-height: 400px;
				}
			}
		`;},
	}),

	inread: mergeDeep(slot_blueprint, {
		getDefaultCss() { return `
			#${this.id} {
				min-height: 400px;
			}
			@media (max-width: 640px) {
				#${this.id} {
					min-height: 400px;
				}
			}
		`;},
	}),

	side: mergeDeep(slot_blueprint, {
		getDefaultCss() { return `
			#${this.id} {
				min-height: 400px;
				width: 100%;
			}

			@media (max-width: 640px) {
				#${this.id} {
					min-height: 400px;
					width: 100%;
				}
			}
		`;},
	}),
	
	footer: mergeDeep(slot_blueprint, {
		getDefaultCss() { return `
			body {
				padding-bottom: 106px;
			}
			#${this.id} {
				padding-top: 0;
				padding-bottom: 0;
				position: fixed;
				bottom: 0;
				left: 0;
				right: 0;
				z-index: 2000;

				min-height: 0;
			}

			@media (max-width: 1024px) {
				body {
					padding-bottom: 165px;
				}
				#${this.id} {
					bottom: 0;
					padding-top: 0;
					padding-bottom: 0;
				}
				
			}
		`;}
	})
}

/** 
 * 
 * DEFAULT SLOT STYLES 
 * 
 * */
function getGlobalStyles(backgroundColor: string = '#eee', customCss: string = ''): string 
{ return `<style>
	:root {
		--bg-color: ${backgroundColor};
	}
	.mg-settings { display: flex; padding: 10px; background-color: #fff; align-items: center; justify-content: center; }
	.mg-slot {
		padding-top: 0;
		padding-bottom: 0;
		background-color: var(--bg-color, #eee);
		position: relative;
		isolation: isolate;
		min-height: 90px;
	}
	.mg-slot > *:not(.mg-powered) {
		display: flex;
		justify-content: center;
		margin: 0 auto;
	}
	.mg-slot > ins {
		display: flex !important;
	}
	.mg-slot iframe { max-width: 100%; display: block; margin: 0 auto; }
	.mg-slot::after { pointer-events: none; }
	.mg-slot::after, .mg-slot>.mg-powered {
		width: 20px;
		height: 18px;
		z-index: -1;
		bottom: 2px;
		right: 2px;

		content: "";
		display: block;
		position: absolute;

		background-image: url("data:image/svg+xml,%3Csvg width='20' height='18' xmlns='http://www.w3.org/2000/svg' xml:space='preserve' style='enable-background:new 0 0 1920 1080' viewBox='505.52 189.83 933.93 627.78'%3E%3ClinearGradient id='a' x1='505.56' x2='1439.423' y1='476.036' y2='476.036' gradientTransform='matrix(1 0 0 -1 0 1082)' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' style='stop-color:%2380408f'/%3E%3Cstop offset='.014' style='stop-color:%23804190'/%3E%3Cstop offset='.347' style='stop-color:%234873b6'/%3E%3Cstop offset='.633' style='stop-color:%232196d3'/%3E%3Cstop offset='.861' style='stop-color:%2318abe2'/%3E%3Cstop offset='1' style='stop-color:%2310b3e8'/%3E%3C/linearGradient%3E%3Cpath d='M1434.8 457.8c14.7-39.4-7.7-63.4-36.4-63.4-9.9 0-17.8 2-27.2 5.8l-234.7 93.6 48.6 73.9 135.5-54.1-61.8 166L1305 750c7.4 11.5 10 29 5.1 42.1l124.7-334.3zm-332.3 142.9-48.6-73.9-391.3 156.1-123.5 49.3c-12.3 4.9-20.3 10.8-25.2 18.2-20.5 31.1-1 67.2 33.4 67.2 7.4 0 15.5-1.5 23.8-4.8l531.4-212.1z' style='fill:url(%23a)'/%3E%3ClinearGradient id='b' x1='684.659' x2='1239.152' y1='640.346' y2='197.7' gradientTransform='matrix(1 0 0 -1 0 1082)' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' style='stop-color:%23e40464'/%3E%3Cstop offset='1' style='stop-color:%235b2675'/%3E%3C/linearGradient%3E%3Cpath d='M1270.5 817.5c18 0 33.2-8.1 39.7-25.4 5-13.1 2.4-30.7-5.1-42.1l-46.2-70.3-73.7-112-48.6-73.9L953 214.7c-22.1-33.6-65.5-32.7-86.9.1L513.9 750.3c4.9-7.4 12.9-13.2 25.2-18.2l123.5-49.3 247-375.4 144.3 219.3 48.6 73.9L1229.7 794c10.6 16.4 24.4 23.5 40.8 23.5z' style='fill:url(%23b)'/%3E%3C/svg%3E");
		background-position: right center;
		background-repeat: no-repeat;
		background-size: contain;
	}

	/*.mg-slot:empty::after { display: none; }*/

	.mg-slot > *:not(.mg-powered) { position: sticky; top: 0; }

	.mg-hidden {
		position: absolute;
		width: 1px;
		height: 1px;
		max-height: 1px !important;
		padding: 0;
		margin: -1px;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		white-space: nowrap;
		border-width: 0;
	}



	/* CUSTOM STYLES FOR THE PUBLISHER */
	${customCss}



	@media (max-width:1024px) {
		/* sticky top op mobiel iets hoger vanwege floating bar */
		.mg-slot > *:not(.mg-powered)  { top: 125px; }
	}

	@media print {
		.mg-slot, .mg-settings { display: none !important; }
	}
</style>`};

// const slotPositions:any = {}
// const slotPath = ''
// 
// addSlotPosition(
//     'top', 
//     {
//         id: 'ada-top',
//         path: slotPath + 'ada-top',
//     },
//     slot_defaults.inread
// )
// 
// function addSlotPosition(key:string, settings:any, mergeWithTheseSettings:any = {}) {
//     slotPositions[key] = prepareSlotPosition(settings, mergeWithTheseSettings);
// }

function prepareSlotPosition(settings:any, mergeWithTheseSettings:any = {}) {
	return mergeDeep(mergeWithTheseSettings, settings);
}

export async function applyBlueprint(publisherConfig: PublisherConfig): Promise<PublisherConfig> {
	// Merge publisher config with blueprint
	const publisher: PublisherConfig = mergeDeep(configBlueprint, publisherConfig ?? {});

	// Apply global styles
	const globalStyles = getGlobalStyles(publisher.config.backgroundColor, publisher.config.customCss);
	if(globalStyles) {
		document.head.insertAdjacentHTML("beforeend", globalStyles);
	}

	const homepages = publisher.config.homepages ?? [];

	const customPageType = publisherConfig.config.customPageType ?? null;
	let pageType = isHome(homepages) ? 'homepage' : 'artikel';
	if (customPageType) {
		pageType = customPageType;
	}

	publisher.config.slotPath += pageType + "/";

	const device = getDeviceType() ?? deviceType.DESKTOP;
	
	const slots: {[key in SlotKeyItterable]?: Slot} = {};
	const adUnits: AdUnit[] = [];

	const slotConfigs = publisher.slots;
	for (const key in slotConfigs) {
		const slotKey = key as SlotKeyItterable;
		if (Object.prototype.hasOwnProperty.call(slotConfigs, slotKey)) {
			const slotConfig = <SlotConfig|undefined> slotConfigs[slotKey];
			if(!slotConfig) continue;

			slotConfig.publisher = publisher;
			
			let slotId = slotKeyToSlotId(slotKey);
			const keyParts = slotKey.split(/(\d+)/, 2);
			const slotType = keyParts[0];

			// // Build slot ID
			// let slotId = 'ada-'+slotType;
			// if(typeof keyParts[1] != 'undefined') {
			// 	slotId += '-'+keyParts[1];
			// }

			// Prepare sizes
			const sizes = parseSizes(slotConfig.sizes, device) ?? [];

			// Parse skip
			let skip = slotConfig.skip ?? false;

			let skipConfig = skip;
			if(!Array.isArray(skipConfig)) {
				skipConfig = [skipConfig];
			}
			
			skip = false;
			for (let i = 0; i < skipConfig.length; i++) {				
				if(parseSkip(skipConfig[i], homepages, device)){
					skip = true;
					break;
				}
			}

			const slot: Slot = prepareSlotPosition(
				{
					...slotConfig, 
					...{
						slotKey: slotKey,
						slotType: slotType,
						id: slotId,
						path: publisher.config.slotPath + slotId,
						sizes: sizes,
						skip: skip,

						publisher: publisher
					}
				},
				slot_defaults[slotType as SlotKey]
			);
			
			slots[slotKey] = slot;

			for (const bidderName in slotConfig.bidders) {
				if (Object.prototype.hasOwnProperty.call(slotConfig.bidders, bidderName)) {
					const bidderValue: any = slotConfig.bidders[bidderName as BidderKey];
					
					if(typeof bidderValue == 'undefined')
						continue;

					try {
						const bidderModule = await import(/* @vite-ignore */`../bidders/${bidderName}.ts`);
						const bidder: BaseBidder = new bidderModule.default(slot, device, bidderValue);
						adUnits.push(bidder.getAdUnit());
					} catch (error) {
						console.error(`Bidder '${bidderName}' not found`, error);
					}
				}
			}

		}
	}

	
	publisher.slots = slots;
	publisher.adUnits = adUnits;

	return publisher;
}

function parseSkip(skipConfig: SkipOption, homepages: string[], device: deviceType): boolean {
	if(!skipConfig) {
		return false;
	}

	if(typeof skipConfig == "boolean") {
		return skipConfig;
	}
	
	let negate = false;
	let skipKey: SkipKey;
	if(skipConfig.charAt(0) == '!') {
		skipKey = skipConfig.substring(1) as SkipKey;
		negate = true;
	}else{
		skipKey = skipConfig as SkipKey;
	}

	let skip = false;
	switch (skipKey) {
		case 'home':
			skip = isHome(homepages);
			break;
		case "desktop":
			skip = (device == deviceType.DESKTOP);
			break;
		case "tablet":
			skip = (device == deviceType.TABLET);
			break;
		case "mobile":
			skip = (device == deviceType.MOBILE);
			break;
	}
	
	if(negate) {
		skip = !skip;
	}

	return skip;
}
